import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Space,
  Form,
  Row,
  Col,
  Input,
  Select,
  message,
  Spin,
  Modal
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminUnitApi, fileApi } from '@/services/api';
import UnitTypeFields from '../components/unit/UnitTypeFields';

const { Title } = Typography;
const { Option } = Select;

const DEFAULT_FREE_WRITING_COUNT = 6;
const MIN_FREE_WRITING_COUNT = 1;
const MAX_FREE_WRITING_COUNT = 10;

const VIDEO_QUIZ_SUPPORTED_TYPES = new Set<string>([
  'PHONICS_DAY',
  'STARTER_PHONICS',
  'STARTER_NUANCE',
  'STARTER_STRUCTURE',
  'ESSAY',
]);

const WORD_CHOICE_SUPPORTED_TYPES = new Set<string>([
  'STARTER_NUANCE',
  'STARTER_STRUCTURE',
]);

const normalizeFreeWritingCount = (value: unknown): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return DEFAULT_FREE_WRITING_COUNT;
  }
  const rounded = Math.round(numeric);
  return Math.min(
    MAX_FREE_WRITING_COUNT,
    Math.max(MIN_FREE_WRITING_COUNT, rounded)
  );
};

const parseFreeWritingHints = (value: unknown): string[] => {
  if (typeof value !== 'string') return [];
  return value
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter(Boolean);
};

interface UnitDetail {
  id: string;
  type: string;
  name: string;
  registrant: string;
  registrationDate: string;
}

const MONTH_NAME_MAP: Record<string, number> = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

const formatUnitTimestamp = (value?: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : String(value);
  }
  if (Array.isArray(value)) {
    const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0] = value as Array<number | undefined>;
    if ([year, month, day].every((v) => typeof v === 'number')) {
      return dayjs(new Date(year as number, (month as number) - 1, day as number, hour ?? 0, minute ?? 0, second ?? 0)).format('YYYY-MM-DD HH:mm');
    }
    return value.join('-');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, any>;
    const nestedDate = obj.date ?? obj.localDate;
    const nestedTime = obj.time ?? obj.localTime;
    const resolveMonthValue = (val: unknown): number | undefined => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') return MONTH_NAME_MAP[val.toUpperCase()] || undefined;
      return undefined;
    };
    const year = obj.year ?? nestedDate?.year;
    const month = resolveMonthValue(obj.monthValue ?? nestedDate?.monthValue ?? nestedDate?.month);
    const day = obj.dayOfMonth ?? nestedDate?.dayOfMonth ?? nestedDate?.day;
    if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
      const hour = obj.hour ?? nestedTime?.hour ?? 0;
      const minute = obj.minute ?? nestedTime?.minute ?? 0;
      const second = obj.second ?? nestedTime?.second ?? 0;
      return dayjs(new Date(year, month - 1, day, hour, minute, second)).format('YYYY-MM-DD HH:mm');
    }
    if (typeof obj.epochSecond === 'number') {
      return dayjs(obj.epochSecond * 1000).format('YYYY-MM-DD HH:mm');
    }
  }
  return String(value);
};

const getDisplayRegistrant = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : '관리자';
};

const AdminUnitDetail: React.FC = () => {
  const navigate = useNavigate();
  const { unitId } = useParams<{ unitId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unitData, setUnitData] = useState<UnitDetail | null>(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [learningHistoryModalVisible, setLearningHistoryModalVisible] = useState(false);
  
  // 동영상 퀴즈 관련 상태
  const [videoQuizzes, setVideoQuizzes] = useState<Array<{
    id: string;
    question: string;
    choices: string[];
    answer: number | number[];
    reason: string;
    allowMultiple: boolean;
  }>>([]);
  
  const isEditMode = !!unitId;
  const [selectedType, setSelectedType] = useState<string>('파닉스데이');
  const [videoQuizEnabled, setVideoQuizEnabled] = useState<boolean>(false);
  const [recordingEnabled, setRecordingEnabled] = useState<boolean>(false);
  const [wordChoiceRecordingEnabled, setWordChoiceRecordingEnabled] = useState<boolean>(false);
const [flashCardInitial, setFlashCardInitial] = useState<Array<{ id?: string; word: string; meaning: string; wordImageUrl?: string }>>([]);
const [wordChoiceInitial, setWordChoiceInitial] = useState<Array<{ id?: string; word: string; wordImageUrl?: string; titleTranslation?: string }>>([]);
const [pendingSubmitValues, setPendingSubmitValues] = useState<any | null>(null);
const [initialStructureSignature, setInitialStructureSignature] = useState<string>('');
  // const [selectedWords, setSelectedWords] = useState<string[]>([]);
  // const [wordChoiceWords, setWordChoiceWords] = useState<string[]>([]);
  // const [wordChoiceRecording, setWordChoiceRecording] = useState<boolean>(false);
  // const [titleTranslation, setTitleTranslation] = useState<string>('');
  // const [correctSentence, setCorrectSentence] = useState<string>('');
  const [diagramCategories, setDiagramCategories] = useState<string[]>(['', '', '', '']);
  const [diagramWords, setDiagramWords] = useState<string[][]>([[], [], [], []]);
  const handleVideoQuizEnabledChange = (enabled: boolean) => {
    setVideoQuizEnabled(enabled);
    if (!enabled) {
      setVideoQuizzes([]);
    }
  };
  // const [fullText, setFullText] = useState<string>('');
  // const [translation, setTranslation] = useState<string>('');
  // const [correctWord, setCorrectWord] = useState<string>('');
  // const [keyword, setKeyword] = useState<string>('');
  // const [writingCount, setWritingCount] = useState<string>('');
  // const [hint, setHint] = useState<string>('');

  // 유형 옵션
  const typeOptions = [
    { value: '파닉스데이', label: '파닉스데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' }
  ];

  // 샘플 데이터 (수정 모드일 때 사용)
  // const sampleUnitData: UnitDetail = { ... };

  const handleBackToList = () => {
    navigate('/admin/unit');
  };

  const mapUnitType = (value: string): string => {
    // 이미 enum이면 그대로
    switch (value) {
      case 'PHONICS_DAY':
      case 'STARTER_PHONICS':
      case 'STARTER_NUANCE':
      case 'STARTER_STRUCTURE':
      case 'ESSAY':
      case 'TRANSLATION':
      case 'READING_BOOK':
        return value;
    }
    // 한글 라벨 → enum
    switch (value) {
      case '파닉스데이': return 'PHONICS_DAY';
      case '스타터_파닉스': return 'STARTER_PHONICS';
      case '스타터_뉘앙스': return 'STARTER_NUANCE';
      case '스타터_스트럭처': return 'STARTER_STRUCTURE';
      case '에세이': return 'ESSAY';
      case '통번역': return 'TRANSLATION';
      case '리딩북': return 'READING_BOOK';
      default: return 'PHONICS_DAY';
    }
  };

  // enum 또는 저장값 → 한글 라벨
  const toUiLabel = (value?: string): string => {
    switch (value) {
      case 'PHONICS_DAY': return '파닉스데이';
      case 'STARTER_PHONICS': return '스타터_파닉스';
      case 'STARTER_NUANCE': return '스타터_뉘앙스';
      case 'STARTER_STRUCTURE': return '스타터_스트럭처';
      case 'ESSAY': return '에세이';
      case 'TRANSLATION': return '통번역';
      case 'READING_BOOK': return '리딩북';
    }
    // 이미 한글 라벨인 경우 그대로 반환
    switch (value) {
      case '파닉스데이':
      case '스타터_파닉스':
      case '스타터_뉘앙스':
      case '스타터_스트럭처':
      case '에세이':
      case '통번역':
      case '리딩북':
        return value;
      default:
        return '파닉스데이';
    }
  };

  const toUnitType = (value?: string): string | undefined => {
    if (!value) return undefined;
    return mapUnitType(value);
  };

  const isVideoQuizSupported = (value?: string): boolean => {
    const unitType = toUnitType(value);
    if (!unitType) return false;
    return VIDEO_QUIZ_SUPPORTED_TYPES.has(unitType);
  };

  const isWordChoiceSupported = (value?: string): boolean => {
    const unitType = toUnitType(value);
    if (!unitType) return false;
    return WORD_CHOICE_SUPPORTED_TYPES.has(unitType);
  };

  const extractFlashCardItems = (values: any) => {
    const items: any[] = [];
    Object.keys(values || {}).forEach((key) => {
      if (key.startsWith('fc_word_')) {
        const id = key.substring('fc_word_'.length);
        const word = values[key];
        const meaning = values[`fc_meaning_${id}`];
        const rawImageUrl = values[`fc_wordImage_${id}`];
        // blob URL인 경우 빈 문자열로 처리 (업로드 실패 방어)
        const wordImageUrl = (rawImageUrl && !rawImageUrl.startsWith('blob:')) ? rawImageUrl : undefined;
        const wordAudioUrl = values[`fc_audioUrl_${id}`]; // ✅ 음원 URL 추가
        const wordId = values[`fc_wordId_${id}`];
        if (word && String(word).trim().length > 0) {
          items.push({
            itemType: 'FLASH_CARD',
            orderIndex: items.length,
            refType: wordId ? 'WORD' : 'NONE',
            refId: wordId || undefined,
            contentJson: JSON.stringify({ word, meaning, wordImageUrl, audioUrl: wordAudioUrl }), // ✅ audioUrl 포함
          });
        }
      }
    });
    return items;
  };

  const filterActivitiesByUnitType = (unitType: string | undefined, activities: any[]): any[] => {
    if (!unitType) {
      return activities || [];
    }
    return (activities || []).filter((activity: any) => {
      if ((activity?.activityType === 'DICTATION' || activity?.activityType === 'VIDEO_QUIZ') && !VIDEO_QUIZ_SUPPORTED_TYPES.has(unitType)) {
        return false;
      }
      if (activity?.activityType === 'WORD_CHOICE' && !WORD_CHOICE_SUPPORTED_TYPES.has(unitType)) {
        return false;
      }
      return true;
    });
  };

  const buildStructureSignatureFromActivities = (activities: any[], unitType?: string): string => {
    const relevantActivities = filterActivitiesByUnitType(unitType, activities);
    const summary = (relevantActivities || []).map((a: any) => ({
      type: a?.activityType || '',
      count: Array.isArray(a?.items) ? a.items.length : 0,
    }));
    return JSON.stringify(summary);
  };

  const buildStructureSignatureFromValues = (values: any): string => {
    const payload = buildPayload(values);
    return buildStructureSignatureFromActivities(payload.activities || [], payload.unitType);
  };

  const buildPayload = (values: any) => {
    const unitCategoryLabel = values?.type || selectedType; // 한글 라벨
    const unitType = mapUnitType(unitCategoryLabel); // 백엔드 enum

    const activities: any[] = [];
    const videoQuizSupported = isVideoQuizSupported(unitType);
    const rawVideoUrl = (values?.videoUrl ?? form.getFieldValue('videoUrl') ?? '').toString().trim();
    const videoQuizFlag = Boolean(values?.videoQuiz ?? videoQuizEnabled);
    const videoQuizActive = videoQuizSupported && (videoQuizFlag || !!rawVideoUrl);
    const wordChoiceSupported = isWordChoiceSupported(unitType);

    // 1) 플래시카드
    const flashCardItems = extractFlashCardItems(values);
    if (flashCardItems.length > 0) {
      activities.push({
        activityType: 'FLASH_CARD',
        title: '플래시 카드',
        instructions: '',
        configJson: JSON.stringify({ recordingEnabled: values?.flashRecordingEnabled ?? recordingEnabled }),
        orderIndex: activities.length,
        items: flashCardItems,
      });
    }

    // 2) 단어고르기 & 녹음(WORD_CHOICE): 스타터 뉘앙스/스타터 스트럭처
    if (wordChoiceSupported) {
      const wcItems: any[] = [];
      const orderField = values?.wc_order;
      const orderedIds = Array.isArray(orderField)
        ? orderField.map((v: any) => String(v)).filter(Boolean)
        : Object.keys(values || {})
            .filter((k) => k.startsWith('wc_word_'))
            .map((k) => k.substring('wc_word_'.length));
      orderedIds.forEach((id: string) => {
        const wcWord = values[`wc_word_${id}`];
        const wcWordId = values[`wc_wordId_${id}`];
        const wcSentenceId = values[`wc_sentenceId_${id}`];
        const rawImageUrl = values[`wc_wordImage_${id}`];
        const wcTitleTranslation = values[`wc_titleTranslation_${id}`];
        // blob URL인 경우 빈 문자열로 처리 (업로드 실패 방어)
        const wordImageUrl = (rawImageUrl && !rawImageUrl.startsWith('blob:')) ? rawImageUrl : '';
        if (wcWord && String(wcWord).trim().length > 0) {
          wcItems.push({
            itemType: 'WORD_CHOICE',
            orderIndex: wcItems.length,
            refType: wcSentenceId ? 'SENTENCE' : (wcWordId ? 'WORD' : 'NONE'),
            refId: wcSentenceId ? Number(wcSentenceId) : (wcWordId || undefined),
            contentJson: JSON.stringify({ word: wcWord, wordImageUrl, titleTranslation: wcTitleTranslation || '' })
          });
        }
      });
      if (wcItems.length > 0) {
        const fallbackTitleTranslation = (() => {
          try {
            const first = wcItems[0]?.contentJson ? JSON.parse(wcItems[0].contentJson) : null;
            return first?.titleTranslation || '';
          } catch (_) {
            return '';
          }
        })();
        activities.push({
          activityType: 'WORD_CHOICE',
          title: '단어고르기 & 녹음',
          instructions: '',
          configJson: JSON.stringify({
            recordingEnabled: values?.wc_recordingEnabled ?? wordChoiceRecordingEnabled,
            titleTranslation: fallbackTitleTranslation
          }),
          orderIndex: activities.length,
          items: wcItems,
        });
      }
    }

    // 3) 스타터 스트럭처: 다이어그램(DIAGRAM)
    if (unitType === 'STARTER_STRUCTURE') {
      // 카테고리/단어 배열을 configJson에 저장
      const diagramCfg = {
        categories: diagramCategories,
        words: diagramWords,
      };
      activities.push({
        activityType: 'DIAGRAM',
        title: '다이어그램',
        instructions: '',
        configJson: JSON.stringify(diagramCfg),
        orderIndex: activities.length,
        items: [],
      });
    }

    // 4) 에세이/통번역: 빈칸 채우기(다중) + 프리라이팅
    if (unitType === 'ESSAY' || unitType === 'TRANSLATION') {
      const normalizeFillBlankItems = (): Array<{
        fullText: string;
        translation: string;
        correctWords: string[];
        recordingEnabled: boolean;
        sentenceId?: number;
        audioUrl?: string;
      }> => {
        const list = Array.isArray(values?.fb_items) ? values.fb_items : [];
        const normalized: any[] = [];
        list.forEach((item: any) => {
          const fullText = trimText(item?.fullText);
          const translation = trimText(item?.translation);
          const correctWords = Array.isArray(item?.correctWords)
            ? item.correctWords.map(trimText).filter(Boolean)
            : [];
          if (!fullText || correctWords.length === 0) return;
          const sentenceId = item?.sentenceId != null && `${item.sentenceId}` !== ''
            ? Number(item.sentenceId)
            : undefined;
          const audioUrl = trimText(item?.audioUrl);
          normalized.push({
            fullText,
            translation,
            correctWords,
            recordingEnabled: !!item?.recordingEnabled,
            sentenceId,
            audioUrl: audioUrl || undefined,
          });
        });

        if (normalized.length > 0) return normalized;

        // 구버전 단일 입력 호환
        const legacyFullText = trimText(values?.fullText);
        const legacyCorrectWords = Array.isArray(values?.fb_correctWords)
          ? values.fb_correctWords.map(trimText).filter(Boolean)
          : [];
        if (!legacyFullText || legacyCorrectWords.length === 0) return [];
        return [{
          fullText: legacyFullText,
          translation: trimText(values?.translation),
          correctWords: legacyCorrectWords,
          recordingEnabled: !!values?.recordingEnabled,
          sentenceId: values?.fb_sentenceId ? Number(values.fb_sentenceId) : undefined,
          audioUrl: trimText(values?.fb_audioUrl) || undefined,
        }];
      };

      const fbList = normalizeFillBlankItems();

      fbList.forEach((fb, idx) => {
        const fbCfg = {
          fullText: fb.fullText,
          translation: fb.translation,
          correctWords: fb.correctWords,
          recordingEnabled: fb.recordingEnabled,
          sentenceId: fb.sentenceId || undefined,
          audioUrl: fb.audioUrl || undefined,
        };

        const fbItems: any[] = [];
        if (fbCfg.fullText && fb.sentenceId) {
          fbItems.push({
            itemType: 'PART_WRITING',
            orderIndex: 0,
            refType: 'SENTENCE',
            refId: Number(fb.sentenceId),
            contentJson: JSON.stringify({
              text: fbCfg.fullText,
              translation: fbCfg.translation,
              audioUrl: fbCfg.audioUrl,
            }),
          });
        }

        activities.push({
          activityType: 'PART_WRITING',
          title: '빈칸 채우기',
          instructions: '',
          configJson: JSON.stringify(fbCfg),
          orderIndex: activities.length,
          items: fbItems,
        });
      });

      const rawKeyword = typeof values?.keyword === 'string' ? values.keyword.trim() : '';
      const rawHint = typeof values?.hint === 'string' ? values.hint.trim() : '';
      const rawWritingCount = values?.writingCount ?? DEFAULT_FREE_WRITING_COUNT;
      const normalizedWritingCount = normalizeFreeWritingCount(rawWritingCount);
      const hasKeyword = rawKeyword.length > 0;
      const hasHint = rawHint.length > 0;
      const hasCustomWritingCount =
        rawWritingCount !== undefined &&
        rawWritingCount !== null &&
        `${rawWritingCount}` !== '' &&
        normalizedWritingCount !== DEFAULT_FREE_WRITING_COUNT;

      const fwCfg = {
        keyword: rawKeyword,
        writingCount: normalizedWritingCount,
        hint: rawHint,
      };
      if (hasKeyword || hasHint || hasCustomWritingCount) {
        activities.push({
          activityType: 'FREE_WRITING',
          title: '프리라이팅',
          instructions: '',
          configJson: JSON.stringify(fwCfg),
          orderIndex: activities.length,
          items: [],
        });
      }
    }

    // 5) 리딩북: 스토리 & 다이얼로그 → sentenceIds를 아이템으로 저장하여 ref 연결
    if (unitType === 'READING_BOOK') {
      const sdSentences: string[] = Array.isArray(values?.sd_sentences) ? values.sd_sentences : [];
      const sdSentenceIds: number[] = Array.isArray(values?.sd_sentenceIds) ? values.sd_sentenceIds : [];
      if (sdSentences.length > 0) {
        const sdItems = sdSentences.map((text, idx) => ({
          itemType: 'STORY_DIALOG',
          orderIndex: idx,
          refType: (sdSentenceIds[idx] != null) ? 'SENTENCE' : 'NONE',
          refId: (sdSentenceIds[idx] != null) ? Number(sdSentenceIds[idx]) : undefined,
          contentJson: JSON.stringify({ sentence: text })
        }));
        activities.push({
          activityType: 'STORY_DIALOG',
          title: '스토리 & 다이얼로그',
          instructions: '',
          configJson: JSON.stringify({ recordingEnabled: values?.sd_recordingEnabled ?? false, sentences: sdSentences, sentenceIds: sdSentenceIds }),
          orderIndex: activities.length,
          items: sdItems,
        });
      }

      // 스토리&다이얼로그_퀘스천 저장: sdq_sentences, sdq_sentenceIds, sdq_answers 사용
      const sdqSentences: string[] = Array.isArray(values?.sdq_sentences) ? values.sdq_sentences : [];
      const sdqSentenceIds: (number | undefined)[] = Array.isArray(values?.sdq_sentenceIds) ? values.sdq_sentenceIds : [];
      const sdqAnswers: string[] = Array.isArray(values?.sdq_answers) ? values.sdq_answers : [];
      const sdqImageUrls: string[] = Array.isArray(values?.sdq_imageUrls) ? values.sdq_imageUrls : [];
      if (sdqSentences.length > 0) {
        const qItems = sdqSentences.map((text, idx) => {
          // blob URL인 경우 빈 문자열로 처리 (업로드 실패 방어)
          const imageUrl = (sdqImageUrls[idx] && !sdqImageUrls[idx].startsWith('blob:')) ? sdqImageUrls[idx] : '';
          return {
            itemType: 'STORY_DIALOG_QA',
            orderIndex: idx,
            refType: (sdqSentenceIds[idx] != null) ? 'SENTENCE' : 'NONE',
            refId: (sdqSentenceIds[idx] != null) ? Number(sdqSentenceIds[idx]) : undefined,
            contentJson: JSON.stringify({ sentence: text, answer: sdqAnswers[idx] || '', imageUrl })
          };
        });
        activities.push({
          activityType: 'STORY_DIALOG_QA',
          title: '스토리&다이얼로그_퀘스천',
          instructions: '',
          configJson: JSON.stringify({ recordingEnabled: values?.sdq_recordingEnabled ?? false }),
          orderIndex: activities.length,
          items: qItems,
        });
      }
    }

    // 5) 동영상 퀴즈 (지원되는 유형에만 노출)
    if (videoQuizActive) {
      const videoUrl = rawVideoUrl;
      const quizzesToSave = videoQuizFlag ? videoQuizzes : [];
      const cfg = {
        videoQuizEnabled: videoQuizFlag,
        videoUrl,
        quizzes: quizzesToSave.map(q => ({
          question: q.question,
          choices: q.choices,
          answer: q.allowMultiple
            ? (Array.isArray(q.answer)
                ? q.answer
                : (typeof q.answer === 'number' ? [q.answer] : []))
            : (typeof q.answer === 'number' ? q.answer : 0),
          reason: q.reason,
          allowMultiple: q.allowMultiple ?? false
        }))
      };
      activities.push({
        activityType: 'DICTATION',
        title: '동영상 퀴즈',
        instructions: '',
        configJson: JSON.stringify(cfg),
        orderIndex: activities.length,
        items: [],
      });
    }

    return {
      code: undefined,
      title: values?.name,
      unitType,
      unitCategory: unitCategoryLabel,
      registrant: values?.registrant || '관리자',
      orderIndex: 0,
      activities,
    };
  };

  const trimText = (value?: string | null) => (value ?? '').toString().trim();

  const showUnitValidationErrors = (messages: string[]) => {
    Modal.error({
      title: '필수 항목을 모두 입력해주세요.',
      content: (
        <div style={{ paddingTop: 8 }}>
          <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
            {messages.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )
    });
  };

  const validateUnitFieldCompleteness = (values: any) => {
    const messages: string[] = [];
    const activeType = typeof values?.type === 'string' && values.type.length > 0 ? values.type : selectedType;
    const videoQuizAllowed = isVideoQuizSupported(activeType);
    const videoUrlValue = trimText(values?.videoUrl ?? form.getFieldValue('videoUrl'));
    const isVideoQuizActive = videoQuizAllowed &&
      (Boolean(values?.videoQuiz ?? videoQuizEnabled) || !!videoUrlValue);

    if (isVideoQuizActive) {
      if (!videoUrlValue) {
        messages.push('동영상 URL을 입력해주세요.');
      }
      if (videoQuizzes.length > 0) {
        videoQuizzes.forEach((quiz, index) => {
          const question = trimText(quiz.question);
          if (!question) {
            messages.push(`동영상 퀴즈 문항 ${index + 1}의 질문을 입력해주세요.`);
          }
          const trimmedChoices = (quiz.choices || []).map((choice) => trimText(choice));
          if (trimmedChoices.length < 2) {
            messages.push(`동영상 퀴즈 문항 ${index + 1}에 보기 2개 이상을 입력해주세요.`);
          }
          if (trimmedChoices.some((choice) => !choice)) {
            messages.push(`동영상 퀴즈 문항 ${index + 1}의 모든 보기를 입력해주세요.`);
          }
          if (quiz.allowMultiple) {
            const answers = Array.isArray(quiz.answer) ? quiz.answer : [];
            if (answers.length === 0) {
              messages.push(`동영상 퀴즈 문항 ${index + 1}의 정답을 선택해주세요.`);
            }
          } else {
            const answerIndex = typeof quiz.answer === 'number' ? quiz.answer : -1;
            if (answerIndex < 0 || answerIndex >= trimmedChoices.length) {
              messages.push(`동영상 퀴즈 문항 ${index + 1}의 정답을 선택해주세요.`);
            }
          }
        });
      }
    }

    if (activeType === '스타터_스트럭처') {
      const missingIndexes = (diagramWords || [])
        .map((words, idx) => {
          const validWords = (words || []).filter((word) => !!trimText(word));
          return validWords.length === 0 ? idx + 1 : null;
        })
        .filter((idx): idx is number => idx != null);
      if (missingIndexes.length > 0) {
        messages.push(`다이어그램 섹션 ${missingIndexes.join(', ')}의 단어를 한 개 이상 등록해주세요.`);
      }
    }

    if (activeType === '리딩북') {
      const sdSentences = form.getFieldValue('sd_sentences');
      const hasStorySentences = Array.isArray(sdSentences) && sdSentences.some((s: string) => !!trimText(s));
      if (!hasStorySentences) {
        messages.push('스토리 & 다이얼로그 문장을 한 개 이상 선택해주세요.');
      }
      const sdqSentences = form.getFieldValue('sdq_sentences');
      const hasStoryQuestions = Array.isArray(sdqSentences) && sdqSentences.some((s: string) => !!trimText(s));
      if (!hasStoryQuestions) {
        messages.push('스토리&다이얼로그_퀘스천 문항을 한 개 이상 등록해주세요.');
      }
    }

    if (activeType === '에세이' || activeType === '통번역') {
      const fbItems = Array.isArray(values?.fb_items) ? values.fb_items : [];
      const hasNewValid = fbItems.some((item: any) =>
        !!trimText(item?.fullText) &&
        Array.isArray(item?.correctWords) &&
        item.correctWords.some((w: string) => !!trimText(w))
      );
      const legacyCorrectWords = Array.isArray(values?.fb_correctWords) ? values.fb_correctWords : [];
      const hasLegacyValid = !!trimText(values?.fullText) &&
        legacyCorrectWords.some((w: string) => !!trimText(w));

      if (!hasNewValid && !hasLegacyValid) {
        messages.push('빈칸 채우기 정답 단어를 한 개 이상 추가해주세요.');
      }

      const rawHint = trimText(values?.hint ?? form.getFieldValue('hint'));
      const rawWritingCount = values?.writingCount ?? form.getFieldValue('writingCount') ?? DEFAULT_FREE_WRITING_COUNT;
      const normalizedWritingCount = normalizeFreeWritingCount(rawWritingCount);
      const hintTokens = parseFreeWritingHints(rawHint);

      if (!rawHint || hintTokens.length === 0) {
        messages.push('프리라이팅 힌트를 입력해주세요.');
      } else if (hintTokens.length !== normalizedWritingCount) {
        messages.push(`프리라이팅 힌트 갯수는 프리라이팅 갯수(${normalizedWritingCount})와 동일해야 합니다.`);
      }
    }

    if (messages.length > 0) {
      showUnitValidationErrors([...new Set(messages)]);
      const error: any = new Error('UNIT_VALIDATION_ERROR');
      error.isValidation = true;
      throw error;
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      validateUnitFieldCompleteness(values);
      if (isEditMode) {
        try {
          const res = await adminUnitApi.hasLearningHistory(unitId);
          const payload = res?.data || res;
          const hasHistory = !!(payload?.data?.hasHistory ?? payload?.hasHistory);
          if (hasHistory) {
            const newStructure = buildStructureSignatureFromValues(values);
            if (initialStructureSignature && newStructure !== initialStructureSignature) {
              Modal.warning({
                title: '학습이력이 있는 유닛',
                content: '학습이력이 있는 유닛은 구조(문항 수, 유형 등)를 변경할 수 없습니다. 내용만 수정할 수 있습니다.',
              });
              return;
            }
            setPendingSubmitValues(values);
            setLearningHistoryModalVisible(true);
            return;
          }
        } catch (_) {}
        setPendingSubmitValues(values);
        await handleLearningHistoryConfirm();
        return;
      }
      setSaving(true);
      // upload images first if any temp File fields exist (use full form values, not only onFinish values)
      const uploadOne = async (file: File) => {
        const data = await fileApi.upload(file, 'unit_words');
        return data?.data?.url || data?.url || '';
      };
      const allVals = form.getFieldsValue(true);
      const fileKeys = Object.keys(allVals || {}).filter(k => k.startsWith('fc_wordImageFile_') || k.startsWith('wc_wordImageFile_') || k.startsWith('sdq_imageFile_'));
      for (const fk of fileKeys) {
        const isFc = fk.startsWith('fc_wordImageFile_');
        const isWc = fk.startsWith('wc_wordImageFile_');
        const id = fk.substring((isFc ? 'fc_wordImageFile_' : (isWc ? 'wc_wordImageFile_' : 'sdq_imageFile_')).length);
        const file = allVals[fk] as File;
        if (file) {
          const url = await uploadOne(file);
          if (isFc) form.setFieldValue(`fc_wordImage_${id}`, url);
          else if (isWc) form.setFieldValue(`wc_wordImage_${id}`, url);
          else {
            // sdq image url 배열: sdq_imageUrls
            const arr = (form.getFieldValue('sdq_imageUrls') as string[] | undefined) || [];
            const idx = Number(id);
            const next = [...arr];
            next[idx] = url;
            form.setFieldValue('sdq_imageUrls', next);
          }
        }
      }
      const payload = buildPayload(form.getFieldsValue(true));
      await adminUnitApi.create(payload);
      setSaving(false);
      setSuccessModalVisible(true);
    } catch (e: any) {
      if (e?.isValidation) {
        setSaving(false);
        return;
      }
      setSaving(false);
      setErrorModalVisible(true);
    }
  };

  const handleSuccessModalOk = () => {
    setSuccessModalVisible(false);
    navigate('/admin/unit');
  };

  const handleErrorModalOk = () => {
    setErrorModalVisible(false);
  };

  const handleLearningHistoryConfirm = async () => {
    try {
      const submittedValues = pendingSubmitValues || form.getFieldsValue(true);
      validateUnitFieldCompleteness(submittedValues);
      setLearningHistoryModalVisible(false);
      setSaving(true);
      await form.validateFields();
      // 최신 폼 값을 기반으로 파일/이미지 업로드 정보를 포함하도록 보정
      const formSnapshot = form.getFieldsValue(true);
      const mergedValues = pendingSubmitValues ? { ...submittedValues, ...formSnapshot } : formSnapshot;
      const uploadOne = async (file: File) => {
        const data = await fileApi.upload(file, 'unit_words');
        return data?.data?.url || data?.url || '';
      };
      const fileKeys = Object.keys(mergedValues || {}).filter((k) =>
        k.startsWith('fc_wordImageFile_') || k.startsWith('wc_wordImageFile_') || k.startsWith('sdq_imageFile_')
      );
      for (const fk of fileKeys) {
        const isFc = fk.startsWith('fc_wordImageFile_');
        const isWc = fk.startsWith('wc_wordImageFile_');
        const id = fk.substring(
          (isFc ? 'fc_wordImageFile_' : (isWc ? 'wc_wordImageFile_' : 'sdq_imageFile_')).length
        );
        const file = mergedValues[fk] as File;
        if (file) {
          const url = await uploadOne(file);
          if (isFc) {
            form.setFieldValue(`fc_wordImage_${id}`, url);
          } else if (isWc) {
            form.setFieldValue(`wc_wordImage_${id}`, url);
          } else {
            const arr = (form.getFieldValue('sdq_imageUrls') as string[] | undefined) || [];
            const idx = Number(id);
            const next = [...arr];
            next[idx] = url;
            form.setFieldValue('sdq_imageUrls', next);
          }
        }
      }
      // 업로드 후 최신 값을 다시 읽어 payload 생성
      const payloadValues = form.getFieldsValue(true);
      const payload = buildPayload(payloadValues);
      await adminUnitApi.update(Number(unitId), payload);
      setPendingSubmitValues(null);
      setSaving(false);
      setSuccessModalVisible(true);
    } catch (e: any) {
      if (e?.isValidation) {
        setSaving(false);
        return;
      }
      setSaving(false);
      setErrorModalVisible(true);
    }
  };

  const handleLearningHistoryCancel = () => {
    setLearningHistoryModalVisible(false);
    setPendingSubmitValues(null);
  };

  const handleCancel = () => {
    navigate('/admin/unit');
  };

  // 동영상 퀴즈 관련 함수들
  const addVideoQuiz = () => {
    if (videoQuizzes.length < 5) {
      const newQuiz = {
        id: `quiz_${Date.now()}`,
        question: '',
        choices: ['', ''],
        answer: 0,
        reason: '',
        allowMultiple: false
      };
      setVideoQuizzes([...videoQuizzes, newQuiz]);
    }
  };

  const removeVideoQuiz = (quizId: string) => {
    setVideoQuizzes(videoQuizzes.filter(quiz => quiz.id !== quizId));
  };

  const updateQuizQuestion = (quizId: string, question: string) => {
    setVideoQuizzes(videoQuizzes.map(quiz => 
      quiz.id === quizId ? { ...quiz, question } : quiz
    ));
  };

  const updateQuizReason = (quizId: string, reason: string) => {
    setVideoQuizzes(videoQuizzes.map(quiz => 
      quiz.id === quizId ? { ...quiz, reason } : quiz
    ));
  };

  const addQuizChoice = (quizId: string) => {
    setVideoQuizzes(videoQuizzes.map(quiz => {
      if (quiz.id === quizId && quiz.choices.length < 5) {
        return { ...quiz, choices: [...quiz.choices, ''] };
      }
      return quiz;
    }));
  };

  const removeQuizChoice = (quizId: string, choiceIndex: number) => {
    setVideoQuizzes(videoQuizzes.map(quiz => {
      if (quiz.id === quizId && quiz.choices.length > 2) {
        const newChoices = quiz.choices.filter((_, i) => i !== choiceIndex);
        if (quiz.allowMultiple) {
          const currentAnswers = Array.isArray(quiz.answer) ? quiz.answer : [];
          const adjustedAnswers = currentAnswers
            .filter((idx) => idx !== choiceIndex)
            .map((idx) => (idx > choiceIndex ? idx - 1 : idx));
          return { ...quiz, choices: newChoices, answer: adjustedAnswers };
        }
        let newAnswer = typeof quiz.answer === 'number' ? quiz.answer : 0;
        if (newAnswer === choiceIndex) {
          newAnswer = 0;
        } else if (newAnswer > choiceIndex) {
          newAnswer = newAnswer - 1;
        }
        return { ...quiz, choices: newChoices, answer: newAnswer };
      }
      return quiz;
    }));
  };

  const updateQuizChoice = (quizId: string, choiceIndex: number, value: string) => {
    setVideoQuizzes(videoQuizzes.map(quiz => {
      if (quiz.id === quizId) {
        const newChoices = [...quiz.choices];
        newChoices[choiceIndex] = value;
        return { ...quiz, choices: newChoices };
      }
      return quiz;
    }));
  };

  const setQuizAnswer = (quizId: string, answerIndex: number) => {
    setVideoQuizzes(videoQuizzes.map(quiz => {
      if (quiz.id !== quizId) return quiz;
      if (quiz.allowMultiple) {
        const currentAnswers = Array.isArray(quiz.answer) ? quiz.answer : [];
        const exists = currentAnswers.includes(answerIndex);
        const nextAnswers = exists
          ? currentAnswers.filter((idx) => idx !== answerIndex)
          : [...currentAnswers, answerIndex];
        return { ...quiz, answer: nextAnswers.sort((a, b) => a - b) };
      }
      return { ...quiz, answer: answerIndex };
    }));
  };

  const toggleQuizMultiple = (quizId: string, allowMultiple: boolean) => {
    setVideoQuizzes(videoQuizzes.map(quiz => {
      if (quiz.id !== quizId) return quiz;
      if (allowMultiple) {
        const asArray = Array.isArray(quiz.answer)
          ? quiz.answer
          : (typeof quiz.answer === 'number' ? [quiz.answer] : []);
        return { ...quiz, allowMultiple: true, answer: asArray };
      }
      const normalized = Array.isArray(quiz.answer)
        ? (quiz.answer[0] ?? 0)
        : (typeof quiz.answer === 'number' ? quiz.answer : 0);
      return { ...quiz, allowMultiple: false, answer: normalized };
    }));
  };

  useEffect(() => {
    const loadDetail = async () => {
      if (!isEditMode) {
        form.setFieldsValue({
          type: '파닉스데이',
          registrant: '관리자',
          writingCount: DEFAULT_FREE_WRITING_COUNT,
          keyword: '',
          hint: ''
        });
        return;
      }
      setLoading(true);
      try {
        const res = await adminUnitApi.detail(unitId!);
        const data = res?.data || res;
        const d = data?.data || data;
        // 상단 기본 필드(백엔드 값 → 한글 라벨로 변환)
        const uiType = toUiLabel(d?.unitCategory || d?.unitType);
        const enumType = mapUnitType(uiType);
        const enumSupportsVideoQuiz = isVideoQuizSupported(enumType);
        const enumSupportsWordChoice = isWordChoiceSupported(enumType);
        setSelectedType(uiType);
        const formattedCreatedAt = formatUnitTimestamp(d?.createdAt);
        const displayRegistrant = getDisplayRegistrant(d?.registrant);
        setUnitData({
          id: String(d?.id || ''),
          type: uiType,
          name: d?.title || '',
          registrant: displayRegistrant,
          registrationDate: formattedCreatedAt
        });
        form.setFieldsValue({
          type: uiType,
          name: d?.title || '',
          registrant: displayRegistrant
        });
        // 액티비티에서 플래시카드/단어고르기/다이어그램/비디오 퀴즈 값 추출
        const activities = Array.isArray(d?.activities) ? d.activities : [];
        setInitialStructureSignature(buildStructureSignatureFromActivities(activities, enumType));
        // 다이어그램 복원 (스타터 스트럭처)
        const dg = activities.find((a: any) => a?.activityType === 'DIAGRAM');
        if (dg) {
          try {
            const cfg = dg?.configJson ? JSON.parse(dg.configJson) : {};
            const cats = Array.isArray(cfg?.categories) ? cfg.categories : ['', '', '', ''];
            const ws = Array.isArray(cfg?.words) ? cfg.words : [[], [], [], []];
            setDiagramCategories(cats);
            setDiagramWords(ws);
            const catValues: Record<string, any> = {};
            cats.forEach((c: string, idx: number) => { catValues[`category_${idx}`] = c || ''; });
            form.setFieldsValue(catValues);
          } catch (_) {}
        } else {
          setDiagramCategories(['', '', '', '']);
          setDiagramWords([[], [], [], []]);
          const catValues: Record<string, any> = {};
          [0,1,2,3].forEach((idx) => { catValues[`category_${idx}`] = ''; });
          form.setFieldsValue(catValues);
        }
        const flash = activities.find((a: any) => a?.activityType === 'FLASH_CARD');
        // 플래시카드 아이템 복원
        if (flash) {
          try {
            const items = Array.isArray(flash?.items) ? flash.items : [];
            const initialWords: Array<{ id?: string; word: string; meaning: string; wordImageUrl?: string; audioUrl?: string }> = [];
            items.forEach((it: any, idx: number) => {
              try {
                const c = it?.contentJson ? JSON.parse(it.contentJson) : {};
                const wid = String(idx + 1);
                initialWords.push({ 
                  id: wid, 
                  word: c?.word || '', 
                  meaning: c?.meaning || '', 
                  wordImageUrl: c?.wordImageUrl || '',
                  audioUrl: c?.audioUrl || '' // ✅ audioUrl 복원
                });
              } catch (_) {}
            });
            // 폼에 세팅
            const values: Record<string, any> = {};
            initialWords.forEach(w => {
              const wid = w.id || '';
              values[`fc_word_${wid}`] = w.word;
              values[`fc_meaning_${wid}`] = w.meaning;
              if (w.wordImageUrl) values[`fc_wordImage_${wid}`] = w.wordImageUrl;
              if (w.audioUrl) values[`fc_audioUrl_${wid}`] = w.audioUrl; // ✅ audioUrl 폼에 세팅
            });
            form.setFieldsValue(values);
            setFlashCardInitial(initialWords);
            // recordingEnabled
            const cfg = flash?.configJson ? JSON.parse(flash.configJson) : {};
            const rec = !!cfg?.recordingEnabled;
            setRecordingEnabled(rec);
            form.setFieldValue('flashRecordingEnabled', rec);
          } catch (_) {}
        }
        // WORD_CHOICE 복원 (스타터 뉘앙스)
        const wc = enumSupportsWordChoice
          ? activities.find((a: any) => a?.activityType === 'WORD_CHOICE')
          : undefined;
        if (enumSupportsWordChoice && wc) {
          try {
            const items = Array.isArray(wc?.items) ? wc.items : [];
            const initialWcWords: Array<{ id?: string; word: string; wordImageUrl?: string; titleTranslation?: string }> = [];
            items.forEach((it: any, idx: number) => {
              const c = it?.contentJson ? JSON.parse(it.contentJson) : {};
              const wid = String(idx + 1);
              initialWcWords.push({ id: wid, word: c?.word || '', wordImageUrl: c?.wordImageUrl || '', titleTranslation: c?.titleTranslation || '' });
            });
            setWordChoiceInitial(initialWcWords);
            const wcValues: Record<string, any> = {};
            initialWcWords.forEach(w => {
              const wid = w.id || '';
              wcValues[`wc_word_${wid}`] = w.word;
              if (w.wordImageUrl) wcValues[`wc_wordImage_${wid}`] = w.wordImageUrl;
              wcValues[`wc_titleTranslation_${wid}`] = w.titleTranslation || '';
            });
            wcValues['wc_order'] = initialWcWords.map((w) => w.id || '').filter(Boolean);
            form.setFieldsValue(wcValues);
            const cfg = wc?.configJson ? JSON.parse(wc.configJson) : {};
            const wcRec = !!cfg?.recordingEnabled;
            setWordChoiceRecordingEnabled(wcRec);
            form.setFieldValue('wc_recordingEnabled', wcRec);
          } catch (_) {}
        } else {
          setWordChoiceInitial([]);
          setWordChoiceRecordingEnabled(false);
          form.setFieldValue('wc_recordingEnabled', false);
          form.setFieldValue('wc_order', ['1']);
        }
        // PART_WRITING(빈칸 채우기) 복원 (에세이/통번역) - 다중 문항
        const pwActs = activities.filter((a: any) => a?.activityType === 'PART_WRITING');
        if (pwActs.length > 0) {
          try {
            const fbItems = pwActs.map((pw: any) => {
              try {
                const cfg = pw?.configJson ? JSON.parse(pw.configJson) : {};
                return {
                  fullText: cfg?.fullText || '',
                  translation: cfg?.translation || '',
                  recordingEnabled: !!cfg?.recordingEnabled,
                  correctWords: Array.isArray(cfg?.correctWords) ? cfg.correctWords : [],
                  sentenceId: cfg?.sentenceId || undefined,
                  audioUrl: cfg?.audioUrl || undefined,
                };
              } catch (_) {
                return {
                  fullText: '',
                  translation: '',
                  recordingEnabled: false,
                  correctWords: [],
                  sentenceId: undefined,
                  audioUrl: undefined,
                };
              }
            });
            form.setFieldsValue({ fb_items: fbItems });
          } catch (_) {}
        } else {
          form.setFieldsValue({ fb_items: [] });
        }
        // FREE_WRITING(프리라이팅) 복원 (에세이)
        const fw = activities.find((a: any) => a?.activityType === 'FREE_WRITING');
        if (fw) {
          try {
            const cfg = fw?.configJson ? JSON.parse(fw.configJson) : {};
            const restoredWritingCount = normalizeFreeWritingCount(
              cfg?.writingCount ?? DEFAULT_FREE_WRITING_COUNT
            );
            form.setFieldsValue({
              keyword: cfg?.keyword || '',
              writingCount: restoredWritingCount,
              hint: cfg?.hint || ''
            });
          } catch (_) {}
        } else {
          form.setFieldsValue({
            keyword: '',
            writingCount: DEFAULT_FREE_WRITING_COUNT,
            hint: ''
          });
        }

        // 리딩북: STORY_DIALOG 복원
        const sdAct = activities.find((a: any) => a?.activityType === 'STORY_DIALOG');
        if (sdAct) {
          try {
            const items = Array.isArray(sdAct?.items) ? sdAct.items : [];
            const sdSentences: string[] = [];
            const sdSentenceIds: number[] = [];
            items.forEach((it: any, idx: number) => {
              try {
                const c = it?.contentJson ? JSON.parse(it.contentJson) : {};
                sdSentences[idx] = c?.sentence || '';
                const refId = it?.refId;
                if (refId != null) sdSentenceIds[idx] = Number(refId);
              } catch (_) {}
            });
            // configJson 기반 보조 복원
            try {
              const cfg = sdAct?.configJson ? JSON.parse(sdAct.configJson) : {};
              const cfgSentences = Array.isArray(cfg?.sentences) ? cfg.sentences : undefined;
              const cfgIds = Array.isArray(cfg?.sentenceIds) ? cfg.sentenceIds : undefined;
              if (cfgSentences && cfgSentences.length > 0) {
                cfgSentences.forEach((t: string, i: number) => { sdSentences[i] = sdSentences[i] || t; });
              }
              if (cfgIds && cfgIds.length > 0) {
                cfgIds.forEach((v: any, i: number) => { if (sdSentenceIds[i] == null && v != null) sdSentenceIds[i] = Number(v); });
              }
              form.setFieldValue('sd_recordingEnabled', !!cfg?.recordingEnabled);
            } catch (_) {}
            form.setFieldValue('sd_sentences', sdSentences);
            form.setFieldValue('sd_sentenceIds', sdSentenceIds);
          } catch (_) {}
        }

        // 리딩북: STORY_DIALOG_QA 복원
        const sdqAct = activities.find((a: any) => a?.activityType === 'STORY_DIALOG_QA');
        if (sdqAct) {
          try {
            const items = Array.isArray(sdqAct?.items) ? sdqAct.items : [];
            const sdqSentences: string[] = [];
            const sdqSentenceIds: number[] = [];
            const sdqAnswers: string[] = [];
            const sdqImageUrls: string[] = [];
            items.forEach((it: any, idx: number) => {
              try {
                const c = it?.contentJson ? JSON.parse(it.contentJson) : {};
                sdqSentences[idx] = c?.sentence || '';
                sdqAnswers[idx] = c?.answer || '';
                sdqImageUrls[idx] = c?.imageUrl || '';
                const refId = it?.refId;
                if (refId != null) sdqSentenceIds[idx] = Number(refId);
              } catch (_) {}
            });
            form.setFieldValue('sdq_sentences', sdqSentences);
            form.setFieldValue('sdq_sentenceIds', sdqSentenceIds);
            form.setFieldValue('sdq_answers', sdqAnswers);
            form.setFieldValue('sdq_imageUrls', sdqImageUrls);
            try {
              const cfg = sdqAct?.configJson ? JSON.parse(sdqAct.configJson) : {};
              form.setFieldValue('sdq_recordingEnabled', !!cfg?.recordingEnabled);
            } catch (_) {}
          } catch (_) {}
        }

        // 비디오 퀴즈
        const vq = activities.find((a: any) => a?.activityType === 'DICTATION' || a?.activityType === 'VIDEO_QUIZ');
        if (enumSupportsVideoQuiz && vq) {
          try {
            const parseJsonSafe = (value: any) => {
              if (!value) return {};
              if (typeof value === 'string') {
                try {
                  return JSON.parse(value);
                } catch (_) {
                  return {};
                }
              }
              if (typeof value === 'object') return value;
              return {};
            };
            const cfg = parseJsonSafe(vq?.configJson);
            let vUrl = cfg?.videoUrl || '';
            let quizzes = Array.isArray(cfg?.quizzes) ? cfg.quizzes : [];
            if ((!vUrl && quizzes.length === 0) && Array.isArray(vq?.items) && vq.items.length > 0) {
              const itemCfg = parseJsonSafe(vq.items[0]?.contentJson);
              vUrl = vUrl || itemCfg?.videoUrl || '';
              if (quizzes.length === 0 && Array.isArray(itemCfg?.quizzes)) {
                quizzes = itemCfg.quizzes;
              }
            }
            const enabled = cfg?.videoQuizEnabled === true || quizzes.length > 0;
            setVideoQuizEnabled(enabled);
            // 내부 상태 업데이트
            setVideoQuizzes(quizzes.map((q: any, idx: number) => {
              const storedAnswer = q?.answer;
              const isMulti = q?.allowMultiple === true || Array.isArray(storedAnswer);
              const normalizedAnswer = isMulti
                ? (Array.isArray(storedAnswer)
                    ? storedAnswer
                        .map((val: any) => {
                          const num = typeof val === 'number' ? val : Number(val);
                          return Number.isNaN(num) ? null : num;
                        })
                        .filter((val): val is number => val !== null)
                    : [])
                : Number(storedAnswer ?? 0);
              const choiceList = Array.isArray(q?.choices) && q.choices.length > 0 ? q.choices : ['', ''];
              return {
                id: `q${idx + 1}`,
                question: q?.question || '',
                choices: choiceList,
                answer: normalizedAnswer,
                reason: q?.reason || '',
                allowMultiple: isMulti
              };
            }));
            form.setFieldValue('videoUrl', vUrl);
            form.setFieldValue('videoQuiz', enabled);
          } catch (_) {}
        } else {
          // 해당 활동이 없으면 비활성으로 표시하거나 유형상 지원하지 않는 경우 초기화
          setVideoQuizEnabled(false);
          setVideoQuizzes([]);
          form.setFieldValue('videoQuiz', false);
          form.setFieldValue('videoUrl', '');
        }
        setLoading(false);
      } catch (e) {
        setLoading(false);
        message.error('유닛 정보를 불러오지 못했습니다.');
      }
    };
    loadDetail();
  }, [unitId, form, isEditMode]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="로딩 중..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <Title level={2}>
            {isEditMode ? '유닛 상세' : '유닛 등록'}
          </Title>
        </div>

        {/* 유닛 정보 폼 */}
        <Form
          form={form}
          layout="vertical"
          initialValues={{ writingCount: DEFAULT_FREE_WRITING_COUNT }}
          onFinish={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const target = e.target as HTMLElement;
              const tag = (target?.tagName || '').toLowerCase();
              const inSelect = !!(target as any)?.closest?.('.ant-select');
              const inPicker = !!(target as any)?.closest?.('.ant-picker');
              if (tag !== 'textarea' && !inSelect && !inPicker) {
                e.preventDefault();
              }
            }
          }}
        >
          <Row gutter={24}>
            <Col span={6}>
              <Form.Item
                label="유형"
                name="type"
                rules={[
                  { required: true, message: '유형을 선택해주세요' }
                ]}
              >
                <Select
                  placeholder="유형을 선택하세요"
                  size="large"
                  disabled={isEditMode}
                  onChange={(value) => {
                    if (isEditMode) return; // 상세보기(수정 모드)에서는 유형 변경 불가
                    setSelectedType(value);
                  }}
                >
                  {typeOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="유닛명"
                name="name"
                rules={[
                  { required: true, message: '유닛명을 입력해주세요' },
                  { min: 1, max: 100, message: '유닛명은 1~100자로 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="유닛명을 입력하세요"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 유형별 입력 항목 */}
          <UnitTypeFields
            selectedType={selectedType}
            videoQuizzes={videoQuizzes}
            videoQuizEnabled={videoQuizEnabled}
            onVideoQuizEnabledChange={handleVideoQuizEnabledChange}
            onAddVideoQuiz={addVideoQuiz}
            onRemoveVideoQuiz={removeVideoQuiz}
            onUpdateQuizQuestion={updateQuizQuestion}
            onUpdateQuizReason={updateQuizReason}
            onAddQuizChoice={addQuizChoice}
            onRemoveQuizChoice={removeQuizChoice}
            onUpdateQuizChoice={updateQuizChoice}
            onSetQuizAnswer={setQuizAnswer}
            onToggleQuizMultiple={toggleQuizMultiple}
            recordingEnabled={recordingEnabled}
            onRecordingEnabledChange={setRecordingEnabled}
            flashRecordingEnabled={recordingEnabled}
            onFlashRecordingEnabledChange={setRecordingEnabled}
            wordChoiceRecordingEnabled={wordChoiceRecordingEnabled}
            onWordChoiceRecordingEnabledChange={setWordChoiceRecordingEnabled}
            onWordImageUpload={(id, url) => form.setFieldValue(`wordImage_${id}`, url)}
            onMeaningImageUpload={(id, url) => form.setFieldValue(`meaningImage_${id}`, url)}
            // onWordSelectionChange={setSelectedWords}
            // onWordChoiceChange={setWordChoiceWords}
            // onWordChoiceRecordingChange={setWordChoiceRecording}
            // onTitleTranslationChange={setTitleTranslation}
            // onCorrectSentenceChange={setCorrectSentence}
            onDiagramCategoryChange={(sectionIndex, category) => {
              const newCategories = [...diagramCategories];
              newCategories[sectionIndex] = category;
              setDiagramCategories(newCategories);
            }}
            onDiagramWordChange={(sectionIndex, words) => {
              const newWords = [...diagramWords];
              newWords[sectionIndex] = words;
              setDiagramWords(newWords);
            }}
            diagramCategories={diagramCategories}
            diagramWords={diagramWords}
            // onFullTextChange={setFullText}
            // onTranslationChange={setTranslation}
            // onCorrectWordChange={setCorrectWord}
            // onKeywordChange={setKeyword}
            // onWritingCountChange={setWritingCount}
            // onHintChange={setHint}
            flashCardInitial={flashCardInitial}
            wordChoiceInitial={wordChoiceInitial}
          />

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="등록일"
              >
                <Input
                  value={isEditMode && unitData ? unitData.registrationDate : ''}
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="등록자"
              >
                <Input
                  value={isEditMode && unitData ? unitData.registrant : ''}
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 버튼 영역 */}
          <div style={{ textAlign: 'right', marginTop: 32 }}>
            <Space size="middle">
              <Button 
                onClick={handleCancel}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                취소
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                {isEditMode ? '수정' : '등록'}
              </Button>
            </Space>
          </div>
        </Form>

        {/* 학습이력 확인 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>학습이력 확인</span>
            </div>
          }
          open={learningHistoryModalVisible}
          onOk={handleLearningHistoryConfirm}
          onCancel={handleLearningHistoryCancel}
          footer={[
            <Button key="cancel" onClick={handleLearningHistoryCancel}>
              취소
            </Button>,
            <Button key="confirm" type="primary" onClick={handleLearningHistoryConfirm}>
              수정하기
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold', color: '#faad14' }}>
              학습이력이 있는 유닛입니다.
            </div>
            <div style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              이 유닛은 학생들이 학습한 기록이 있습니다.<br />
              수정하시겠습니까?
            </div>
            <div style={{ 
              backgroundColor: '#fff7e6', 
              border: '1px solid #ffd591', 
              borderRadius: '6px', 
              padding: '12px',
              fontSize: '13px',
              color: '#d46b08'
            }}>
              ⚠️ 수정 시 기존 학습 데이터에 영향을 줄 수 있습니다.
            </div>
          </div>
        </Modal>

        {/* 성공 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
              <span>저장 완료</span>
            </div>
          }
          open={successModalVisible}
          onOk={handleSuccessModalOk}
          onCancel={handleSuccessModalOk}
          footer={[
            <Button key="ok" type="primary" onClick={handleSuccessModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              유닛이 성공적으로 {isEditMode ? '수정' : '등록'}되었습니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              {isEditMode ? '수정된 유닛은 유닛 목록에서 확인할 수 있습니다.' : '등록된 유닛은 유닛 목록에서 확인할 수 있습니다.'}
            </div>
          </div>
        </Modal>

        {/* 실패 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
              <span>저장 실패</span>
            </div>
          }
          open={errorModalVisible}
          onOk={handleErrorModalOk}
          onCancel={handleErrorModalOk}
          footer={[
            <Button key="ok" type="primary" danger onClick={handleErrorModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold', color: '#ff4d4f' }}>
              유닛 {isEditMode ? '수정' : '등록'}에 실패했습니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              다시 시도해주세요.
            </div>
          </div>
        </Modal>

        <Modal
          open={saving}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={420}
        >
          <div style={{ textAlign: 'center', padding: '24px 12px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>
              유닛을 저장하는 중입니다
            </div>
            <div style={{ marginTop: 8, color: '#666', fontSize: 13 }}>
              활동 수와 첨부 자원이 많을 경우 수 분이 걸릴 수 있습니다. 페이지를 새로고침하지 말고 기다려주세요.
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminUnitDetail; 